<script setup lang="ts">
/**
 * Composant InteriorViewer3D - Visualiseur intérieur 360° avec cubemap Three.js
 * Affiche une vue immersive de l'intérieur du véhicule avec contrôles orbitaux
 */

import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import { useInteriorScene } from '@/composables/useInteriorScene'
import { LoadingSpinner } from '@/components/common'
import type { CubeMapFaces, UseInteriorSceneOptions } from '@/types/interior.types'

defineOptions({ name: 'InteriorViewer3D' })

/**
 * Props du composant
 */
interface Props {
  /** URLs des 6 faces du cubemap (objet ou tableau) */
  cubeMapFaces: CubeMapFaces | string[]
  /** Options de configuration du composable */
  sceneOptions?: UseInteriorSceneOptions
  /** Activer l'animation au survol uniquement */
  hoverActivation?: boolean
  /** Afficher l'indicateur de chargement */
  showLoading?: boolean
  /** Classe CSS additionnelle */
  class?: string
}

const props = withDefaults(defineProps<Props>(), {
  hoverActivation: true,
  showLoading: true,
  sceneOptions: () => ({})
})

/**
 * Événements émis par le composant
 */
interface Emits {
  (e: 'loadProgress', progress: number): void
  (e: 'loadComplete'): void
  (e: 'error', error: string): void
  (e: 'ready'): void
}

const emit = defineEmits<Emits>()

// Référence au conteneur DOM
const containerRef = ref<HTMLElement>()

// Composable de gestion de la scène intérieure
const {
  isReady,
  isLoading,
  loadProgress,
  error,
  init,
  loadCubeMap,
  activate,
  deactivate,
  dispose
} = useInteriorScene(props.sceneOptions)

/**
 * Normaliser les faces du cubemap
 * Convertir tableau en objet CubeMapFaces si nécessaire
 */
const normalizedFaces = computed((): CubeMapFaces | string[] => {
  return props.cubeMapFaces
})

/**
 * Gérer le survol pour activer/désactiver le plein framerate
 */
const handleMouseEnter = (): void => {
  if (props.hoverActivation) {
    activate()
  }
}

const handleMouseLeave = (): void => {
  if (props.hoverActivation) {
    deactivate()
  }
}

/**
 * Lifecycle: Initialisation au montage
 */
onMounted(async () => {
  console.log('[DEBUG InteriorViewer3D] onMounted - props.cubeMapFaces:', props.cubeMapFaces)
  console.log('[DEBUG InteriorViewer3D] Type de cubeMapFaces:', Array.isArray(props.cubeMapFaces) ? 'array' : typeof props.cubeMapFaces)
  
  if (!containerRef.value) {
    emit('error', 'Conteneur non disponible')
    return
  }

  try {
    // Initialiser la scène Three.js
    init(containerRef.value)

    console.log('[DEBUG InteriorViewer3D] normalizedFaces.value:', normalizedFaces.value)
    console.log('[DEBUG InteriorViewer3D] Avant loadCubeMap - Type:', Array.isArray(normalizedFaces.value) ? 'array' : typeof normalizedFaces.value)
    
    // Charger le cubemap
    await loadCubeMap(normalizedFaces.value)

    // Émettre l'événement ready
    if (isReady.value) {
      emit('ready')
    }
  } catch (err) {
    const errMsg = err instanceof Error ? err.message : 'Erreur inconnue'
    console.error('[DEBUG InteriorViewer3D] Erreur dans onMounted:', err)
    emit('error', errMsg)
  }
})

/**
 * Lifecycle: Nettoyage au démontage
 */
onUnmounted(() => {
  dispose()
})

/**
 * Watcher: Progression du chargement
 */
watch(loadProgress, (progress) => {
  emit('loadProgress', progress)

  // Émettre l'événement de completion à 100%
  if (progress >= 100) {
    emit('loadComplete')
  }
})

/**
 * Watcher: Erreurs
 */
watch(error, (err) => {
  if (err) {
    emit('error', err)
  }
})

/**
 * Watcher: Changements de cubemap
 */
watch(() => props.cubeMapFaces, async (newFaces) => {
  console.log('[DEBUG InteriorViewer3D] Watch cubeMapFaces déclenché:', newFaces)
  console.log('[DEBUG InteriorViewer3D] Type:', Array.isArray(newFaces) ? 'array' : typeof newFaces)
  
  if (isReady.value && newFaces) {
    try {
      await loadCubeMap(newFaces)
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : 'Erreur inconnue'
      console.error('[DEBUG InteriorViewer3D] Erreur dans watch:', err)
      emit('error', errMsg)
    }
  }
}, { deep: true })
</script>

<template>
  <div
    ref="containerRef"
    :class="['interior-viewer-3d', props.class]"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <!-- Overlay de chargement -->
    <div v-if="showLoading && isLoading" class="interior-loading">
      <LoadingSpinner size="large" />
      <span class="loading-text">{{ Math.round(loadProgress) }}%</span>
    </div>

    <!-- Message d'erreur -->
    <div v-if="error" class="interior-error">
      <p class="error-text">{{ error }}</p>
    </div>
  </div>
</template>

<style scoped>
.interior-viewer-3d {
  position: relative;
  width: 100%;
  max-width: 875px;
  height: auto;
  max-height: 525px;
  aspect-ratio: 875 / 525;
  overflow: hidden;
  background-color: var(--color-bg-stage, #000000);
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  cursor: grab;
}

.interior-viewer-3d:active {
  cursor: grabbing;
}

/* Forcer le canvas Three.js à remplir le conteneur */
.interior-viewer-3d canvas {
  display: block;
  width: 100% !important;
  height: 100% !important;
}

.interior-loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md, 1rem);
  background-color: rgba(255, 255, 255, var(--opacity-overlay, 0.95));
  z-index: var(--z-indicator, 100);
  pointer-events: none;
}

.loading-text {
  font-family: var(--font-family-base, Arial, Helvetica, sans-serif);
  font-size: var(--font-size-lg, 1.125rem);
  font-weight: var(--font-weight-bold, 700);
  color: var(--color-text-primary, #333333);
}

.interior-error {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: var(--spacing-lg, 1.5rem);
  background-color: rgba(255, 0, 0, 0.1);
  border: 2px solid #ff0000;
  border-radius: 4px;
  z-index: var(--z-indicator, 100);
}

.error-text {
  margin: 0;
  font-family: var(--font-family-base, Arial, Helvetica, sans-serif);
  font-size: var(--font-size-md, 1rem);
  color: #ff0000;
  text-align: center;
}

/* Optimisations mobiles */
@media (max-width: 768px) {
  .interior-viewer-3d {
    touch-action: none;
    -webkit-touch-callout: none;
  }
}
</style>